import type { Json, SiteConfig } from "../types";

export interface PageScanOptions {
  emit: (eventName: string, props: Record<string, Json>) => void;
  getConfig: () => SiteConfig | null;
}

function hasMatch(selector: string): boolean {
  try {
    return !!document.querySelector(selector);
  } catch {
    return false;
  }
}

function ctaSelectors(config: SiteConfig | null): string[] {
  const selectors: string[] = [];
  if (config && config.custom_goals) {
    for (const g of config.custom_goals) {
      if (g && g.selector && (g.event_name === "cta_click" || g.event_name === "booking_click")) {
        selectors.push(g.selector);
      }
    }
  }
  // Built-in CTA proxies.
  selectors.push(
    "a[href^='tel:']",
    "a[href*='wa.me']",
    "a[href*='api.whatsapp.com']",
    "a[href*='t.me']",
    "a[href*='telegram.me']",
    "a[href*='yclients']",
    "a[href*='dikidi']",
    "a[href*='/booking']",
  );
  return selectors;
}

function topPxOfFirstCta(selectors: string[]): number | null {
  for (const sel of selectors) {
    try {
      const el = document.querySelector(sel) as HTMLElement | null;
      if (el) {
        const rect = el.getBoundingClientRect();
        return Math.round(rect.top + window.scrollY);
      }
    } catch {
      // skip invalid selector
    }
  }
  return null;
}

function countCtas(selectors: string[]): number {
  let count = 0;
  for (const sel of selectors) {
    try {
      count += document.querySelectorAll(sel).length;
    } catch {
      // skip
    }
  }
  return count;
}

export function runPageScan(opts: PageScanOptions): void {
  const config = opts.getConfig();
  if (config && config.tracking && config.tracking.page_scan === false) return;

  const h1s = document.querySelectorAll("h1");
  const selectors = ctaSelectors(config);
  const firstCtaTop = topPxOfFirstCta(selectors);
  const viewport = window.innerHeight || document.documentElement.clientHeight;

  opts.emit("page_scan", {
    has_h1: h1s.length > 0,
    h1_count: h1s.length,
    has_meta_description: hasMatch("meta[name='description']"),
    has_canonical: hasMatch("link[rel='canonical']"),
    has_phone_link: hasMatch("a[href^='tel:']"),
    has_whatsapp_link: hasMatch("a[href*='wa.me'], a[href*='api.whatsapp.com'], a[href*='web.whatsapp.com']"),
    has_telegram_link: hasMatch("a[href*='t.me'], a[href*='telegram.me']"),
    has_booking_link: hasMatch("a[href*='yclients'], a[href*='dikidi'], a[href*='/booking']"),
    has_form: hasMatch("form"),
    cta_count: countCtas(selectors),
    first_cta_above_fold: firstCtaTop !== null ? firstCtaTop < viewport : false,
    first_cta_top_px: firstCtaTop,
  });
}

export function scheduleInitialPageScan(opts: PageScanOptions): void {
  const run = () => {
    try {
      runPageScan(opts);
    } catch {
      // pixel must never throw on host page
    }
  };
  if (document.readyState === "complete" || document.readyState === "interactive") {
    setTimeout(run, 0);
  } else {
    document.addEventListener("DOMContentLoaded", run, { once: true });
  }
}
